// ═══ AUDIT TRAIL SYSTEM — SEC-09: Tamper-Evident Cryptographic Chaining ═══
// Each audit entry includes a SHA-256 hash of the previous entry,
// creating a tamper-evident chain. If any entry is modified or deleted,
// the chain breaks and can be detected by verification.
//
// FOLLOWUP-#3: audit log is now encrypted at rest via PersistentStore.
// Entries can contain patient names, CRNs, and usernames, so the local
// localStorage copy must be encrypted (matches the existing classification
// of `audit-log` in PersistentStore's SENSITIVE_KEYS set).

import { SessionManager } from './SessionManager.js';
import { API_BASE } from './constants.js';
import { PersistentStore } from './PersistentStore.js';

// Key passed to PersistentStore (it adds the 'sykes-' prefix internally).
// _LS_KEY is the full localStorage key, used only for the raw-data peek
// in the lockout-detection path below.
const AUDIT_KEY = 'audit-log';
const AUDIT_LS_KEY = 'sykes-audit-log';

async function sha256(message) {
  try {
    const encoded = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback: simple hash if WebCrypto unavailable
    let hash = 0;
    for (let i = 0; i < message.length; i++) {
      hash = ((hash << 5) - hash) + message.charCodeAt(i);
      hash |= 0;
    }
    return 'FALLBACK-' + Math.abs(hash).toString(16);
  }
}

// Module-level auth token — each portal's auth hook should call
// AuditLogger.setAuthToken(token) whenever its token state changes
// (on login, logout, and refresh).
let _authToken = null;

const AuditLogger = {
  _key: AUDIT_LS_KEY, // kept for backward-compat readers that reference this directly

  /** SEC-09: Set JWT for server-side audit pushes. Pass null to clear on logout. */
  setAuthToken(token) {
    _authToken = token || null;
  },

  /**
   * Log an audit event. BUG-13 (3 May 2026): added optional patient_id
   * parameter (5th positional, default null) for patient-scoped events
   * (form_open, form_save, form_download, patient_open, etc.). NULL is fine
   * for non-patient events (login/logout, navigation between non-patient
   * sections, governance actions). Server-side foreign-keys to patients(id)
   * so passing an integer for a patient that doesn't exist will fail
   * silently at the DB layer (caller is not informed).
   */
  async log(action, portal, user, detail, patientId = null) {
    try {
      const existing = await this._getLog();

      const buildEntry = (prevHash) => ({
        id: 'AUD-' + Date.now() + '-' + Math.random().toString(36).slice(2, 5),
        ts: new Date().toISOString(),
        action,
        portal,
        user: user || 'system',
        detail: detail || '',
        patientId: patientId || null,  // BUG-13: included in chain hash
        device: SessionManager.getDeviceId(),
        prevHash  // SEC-09: Hash chain link
      });

      // FOLLOWUP-#3: if the log on disk is encrypted but Encryption isn't
      // initialised (e.g., pre-login log call after a fresh page load),
      // _getLog returns null. Skip the local write so we don't clobber the
      // encrypted history with a fresh plaintext array. The backend push
      // still records the entry, so nothing is truly lost.
      if (existing === null) {
        const entry = buildEntry('NO_CHAIN');
        this._pushToBackend(entry).catch(() => {});
        return entry;
      }

      // SEC-09: Compute hash of previous entry for tamper-evident chaining
      const prevHash = existing.length > 0
        ? await sha256(JSON.stringify(existing[0]))
        : 'GENESIS';
      const entry = buildEntry(prevHash);

      existing.unshift(entry);
      // Keep last 2000 entries
      if (existing.length > 2000) existing.length = 2000;

      // FOLLOWUP-#3: route through PersistentStore.saveAsync so the log is
      // encrypted at rest when Encryption is initialised. saveAsync falls
      // back to plaintext if Encryption isn't enabled, which is the correct
      // behaviour for genuinely-empty new logs (no PII at risk yet).
      await PersistentStore.saveAsync(AUDIT_KEY, existing);

      // SEC-09: Also attempt to push to backend (append-only server-side storage)
      this._pushToBackend(entry).catch(() => {});

      return entry;
    } catch (e) { console.warn('Audit log error'); return null; }
  },

  /** SEC-09: Push audit entry to backend PostgreSQL (append-only) */
  async _pushToBackend(entry) {
    // Skip if no auth token — local log is the primary record, and pushing
    // without auth would just 401 and pollute server logs
    if (!_authToken) return;
    try {
      await fetch(`${API_BASE}/audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${_authToken}`
        },
        body: JSON.stringify(entry),
        signal: AbortSignal.timeout(5000)
      });
    } catch { /* Backend unavailable — local log is primary */ }
  },

  /**
   * Read the audit log from PersistentStore.
   * Returns:
   *   []     — genuinely empty (no data on disk)
   *   array  — successfully loaded entries
   *   null   — data exists on disk but couldn't be loaded (typically
   *            encrypted ciphertext with Encryption not yet initialised).
   *            Callers must NOT overwrite in this case.
   */
  async _getLog() {
    try {
      const log = await PersistentStore.loadAsync(AUDIT_KEY);
      if (log === null) {
        // Distinguish "no data on disk" from "load failed".
        const raw = (typeof localStorage !== 'undefined') ? localStorage.getItem(AUDIT_LS_KEY) : null;
        return raw ? null : [];
      }
      return Array.isArray(log) ? log : [];
    } catch { return null; }
  },

  async getEntries(filter) {
    const log = await this._getLog();
    if (!Array.isArray(log)) return []; // null → couldn't load → empty for UI
    if (!filter) return log;
    return log.filter(e => {
      if (filter.portal && e.portal !== filter.portal) return false;
      if (filter.action && e.action !== filter.action) return false;
      if (filter.after && new Date(e.ts) < new Date(filter.after)) return false;
      if (filter.before && new Date(e.ts) > new Date(filter.before)) return false;
      if (filter.search && !JSON.stringify(e).toLowerCase().includes(filter.search.toLowerCase())) return false;
      return true;
    });
  },

  /** SEC-09: Verify chain integrity — returns { valid, brokenAt } */
  async verifyChain() {
    const log = await this._getLog();
    if (!Array.isArray(log)) return { valid: false, brokenAt: -1, error: 'log inaccessible' };
    if (log.length < 2) return { valid: true, brokenAt: null };
    for (let i = 0; i < log.length - 1; i++) {
      const expected = await sha256(JSON.stringify(log[i + 1]));
      if (log[i].prevHash !== expected) {
        return { valid: false, brokenAt: i, entry: log[i] };
      }
    }
    return { valid: true, brokenAt: null };
  },

  async exportCSV() {
    const log = await this._getLog();
    const rows = (Array.isArray(log) ? log : []).map(e =>
      [e.id, e.ts, e.action, e.portal, e.user, '"' + (e.detail || '').replace(/"/g, '""') + '"', e.patientId || '', e.device, e.prevHash || ''].join(',')
    );
    const header = 'ID,Timestamp,Action,Portal,User,Detail,PatientId,Device,PrevHash';
    return header + '\n' + rows.join('\n');
  },

  async clear() {
    await PersistentStore.saveAsync(AUDIT_KEY, []);
  }
};

export { AuditLogger };
